import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { button3DClass } from "@/components/button-3d";
import { plans } from "@/data/site-content";

type Search = { plan?: string };


export const Route = createFileRoute("/contact")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    plan: typeof search.plan === "string" ? search.plan : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Hire a Digital Marketing Agency | Contact MG Content Labs" },
      {
        name: "description",
        content:
          "Hire a B2B digital marketing agency in India. Book a free 30-minute strategy call — we audit your content engine and map three growth plays to ship.",
      },
      { property: "og:title", content: "Hire a Digital Marketing Agency | MG Content Labs" },
      { property: "og:description", content: "Book a free 30-minute content strategy call with our Delhi-based digital marketing team." },
      { property: "og:url", content: "https://mgcontentlabs.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "MG Content Labs",
          description:
            "Digital marketing agency specializing in semantic SEO, AEO, editorial strategy, and content optimization.",
          url: "https://mgcontentlabs.com/",
          image: "https://mgcontentlabs.com/favicon.png",
          email: "hello@mgcontentlabs.com",
          address: {
            "@type": "PostalAddress",
            addressLocality: "New Delhi",
            addressRegion: "Delhi",
            addressCountry: "IN",
          },
          areaServed: "Worldwide",
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const { plan } = Route.useSearch();
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Contact</span>
            <h1 className="mt-4 font-serif text-5xl md:text-6xl">
              Hire a digital marketing agency <span className="italic text-brand-accent">in India.</span>
            </h1>
            <p className="mt-6 max-w-md text-lg text-brand-deep/70">
              Tell us about your business. We'll respond within one business day with a proposed
              agenda for a 30-minute strategy call.
            </p>
            <dl className="mt-12 space-y-6 text-sm">
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-deep/60">Email</dt>
                <dd className="mt-1 font-medium">hello@mgcontentlabs.com</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-deep/60">Response time</dt>
                <dd className="mt-1 font-medium">Within one business day</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-deep/60">Studio</dt>
                <dd className="mt-1 font-medium">Remote worldwide · HQ in Delhi, India</dd>
              </div>
            </dl>
          </div>

          <div className="bg-brand-muted p-8 md:p-12">
            {submitted ? (
              <div className="py-24 text-center">
                <h2 className="font-serif text-3xl">Your email app is ready.</h2>
                <p className="mt-4 text-brand-deep/60">
                  We've opened a pre-filled email to mgcontentservices@gmail.com. Hit send and we'll reply within one business day.
                </p>
              </div>
            ) : (
              <form
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const name = String(formData.get("name") ?? "");
                  const email = String(formData.get("email") ?? "");
                  const company = String(formData.get("company") ?? "");
                  const plan = String(formData.get("plan") ?? "");
                  const message = String(formData.get("message") ?? "");

                  const subject = `New inquiry from ${name || "MG Content Labs website"}`;
                  const body = [
                    `Name: ${name}`,
                    `Email: ${email}`,
                    `Company / URL: ${company || "N/A"}`,
                    `Interested plan: ${plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "Not sure yet"}`,
                    "",
                    "What they are trying to solve:",
                    message,
                  ].join("\n");

                  const mailto = `mailto:mgcontentservices@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  const link = document.createElement("a");
                  link.href = mailto;
                  link.click();

                  setSubmitted(true);
                }}
              >
                <h2 className="font-serif text-3xl">Request a Strategy Audit</h2>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="text-[10px] font-bold uppercase tracking-widest">Full Name</label>
                    <input id="contact-name" required type="text" name="name" className="mt-2 w-full border-b border-brand-deep/20 bg-transparent py-2 outline-none focus:border-brand-accent" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-[10px] font-bold uppercase tracking-widest">Work Email</label>
                    <input id="contact-email" required type="email" name="email" className="mt-2 w-full border-b border-brand-deep/20 bg-transparent py-2 outline-none focus:border-brand-accent" />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-company" className="text-[10px] font-bold uppercase tracking-widest">Company URL</label>
                  <input id="contact-company" type="text" name="company" className="mt-2 w-full border-b border-brand-deep/20 bg-transparent py-2 outline-none focus:border-brand-accent" />
                </div>
                <div>
                  <label htmlFor="contact-plan" className="text-[10px] font-bold uppercase tracking-widest">Interested Plan</label>
                  <select
                    id="contact-plan"
                    name="plan"
                    defaultValue={plan ?? ""}
                    className="mt-2 w-full border-b border-brand-deep/20 bg-transparent py-2 outline-none focus:border-brand-accent"
                  >
                    <option value="">Not sure yet</option>
                    {plans.map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.price.usd === "Custom"
                          ? `${p.name} — Custom`
                          : `${p.name} — ${p.price.usd}/mo (${p.price.inr}/mo)`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="text-[10px] font-bold uppercase tracking-widest">What are you trying to solve?</label>
                  <textarea id="contact-message" name="message" rows={4} className="mt-2 w-full border-b border-brand-deep/20 bg-transparent py-2 outline-none focus:border-brand-accent" />
                </div>
                <button className={button3DClass("primary", "default", true)}>
                  Submit Inquiry
                </button>
                <p className="text-[10px] uppercase tracking-widest text-brand-deep/80">
                  By submitting, you agree to our Privacy Policy.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
